import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import styles from './QuickActionsModal.module.css';
import { 
  Search, Calendar, ScrollText, Users, Briefcase, Home, 
  Settings, User, Rocket, TrendingUp, X, Command, BookOpen
} from 'lucide-react';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  keywords: string[];
  category: 'navigation' | 'actions' | 'profile' | 'articles';
  requiresAuth?: boolean;
  requiresRole?: string[];
  requiresFlag?: string[];
}

interface QuickActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userRole?: string;
  userFlags?: string[];
}

const QuickActionsModal: React.FC<QuickActionsModalProps> = ({ 
  isOpen, 
  onClose, 
  userRole = 'Prospect',
  userFlags = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [kbArticles, setKbArticles] = useState<QuickAction[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Define all available quick actions
  const getAllActions = (): QuickAction[] => [
    // Navigation Actions
    {
      id: 'nav-home',
      title: 'Go to Home',
      description: 'Return to the homepage',
      icon: <Home size={20} />,
      action: () => navigate('/'),
      keywords: ['home', 'main', 'start'],
      category: 'navigation'
    },
    {
      id: 'nav-dashboard',
      title: 'Go to Dashboard',
      description: 'View your personal dashboard',
      icon: <User size={20} />,
      action: () => navigate('/dashboard'),
      keywords: ['dashboard', 'profile', 'my'],
      category: 'navigation',
      requiresAuth: true
    },
    {
      id: 'nav-events',
      title: 'Browse Events',
      description: 'View upcoming organization events',
      icon: <Calendar size={20} />,
      action: () => navigate('/events'),
      keywords: ['events', 'calendar', 'operations', 'ops'],
      category: 'navigation',
      requiresFlag: ['OrgMember']
    },
    {
      id: 'nav-missions',
      title: 'Browse Missions',
      description: 'View and accept available missions',
      icon: <ScrollText size={20} />,
      action: () => navigate('/missions'),
      keywords: ['missions', 'tasks', 'objectives'],
      category: 'navigation',
      requiresFlag: ['OrgMember']
    },
    {
      id: 'nav-members',
      title: 'Member Directory',
      description: 'Browse organization members',
      icon: <Users size={20} />,
      action: () => navigate('/members'),
      keywords: ['members', 'directory', 'people', 'roster'],
      category: 'navigation',
      requiresFlag: ['OrgMember']
    },
    {
      id: 'nav-departments',
      title: 'View Departments',
      description: 'Explore departments and positions',
      icon: <Briefcase size={20} />,
      action: () => navigate('/departments'),
      keywords: ['departments', 'positions', 'roles', 'jobs'],
      category: 'navigation',
      requiresFlag: ['OrgMember']
    },
    {
      id: 'nav-org-points',
      title: 'ORG Points & Levels',
      description: 'View progression system and rewards',
      icon: <TrendingUp size={20} />,
      action: () => navigate('/general/org-points'),
      keywords: ['points', 'org', 'levels', 'progression', 'rewards'],
      category: 'navigation'
    },
    {
      id: 'nav-knowledge-base',
      title: 'Knowledge Base',
      description: 'Browse guides, tutorials, and documentation',
      icon: <ScrollText size={20} />,
      action: () => navigate('/knowledge'),
      keywords: ['knowledge', 'kb', 'articles', 'guides', 'tutorials', 'docs', 'help', 'wiki'],
      category: 'navigation'
    },
    {
      id: 'nav-kb-admin',
      title: 'Knowledge Base Admin',
      description: 'Create and manage KB articles',
      icon: <Settings size={20} />,
      action: () => navigate('/knowledge/admin'),
      keywords: ['knowledge', 'admin', 'create', 'article', 'manage', 'edit'],
      category: 'actions',
      requiresRole: ['Leader', 'Admin']
    },

    // Profile Actions
    {
      id: 'action-edit-profile',
      title: 'Edit Profile',
      description: 'Update your profile information',
      icon: <User size={20} />,
      action: () => {
        navigate('/dashboard');
        // Could add logic to auto-switch to profile tab
      },
      keywords: ['profile', 'edit', 'update', 'settings'],
      category: 'profile',
      requiresAuth: true
    },
    {
      id: 'action-preferences',
      title: 'Set Preferences',
      description: 'Configure your org journey preferences',
      icon: <Rocket size={20} />,
      action: () => {
        navigate('/dashboard');
        // Could add logic to open preferences modal
      },
      keywords: ['preferences', 'interests', 'playstyle'],
      category: 'profile',
      requiresAuth: true
    },
    {
      id: 'action-settings',
      title: 'Settings',
      description: 'Manage account settings',
      icon: <Settings size={20} />,
      action: () => {
        navigate('/dashboard');
      },
      keywords: ['settings', 'account', 'password', 'theme'],
      category: 'profile',
      requiresAuth: true
    },

    // Leader Actions
    {
      id: 'action-leader-dashboard',
      title: 'Leader Dashboard',
      description: 'Access leadership tools and approvals',
      icon: <Briefcase size={20} />,
      action: () => navigate('/leader-dashboard'),
      keywords: ['leader', 'admin', 'manage', 'approvals'],
      category: 'actions',
      requiresRole: ['Leader', 'Admin']
    },
    {
      id: 'action-create-mission',
      title: 'Create Mission',
      description: 'Create a new organization mission',
      icon: <ScrollText size={20} />,
      action: () => navigate('/leader-dashboard/missions'),
      keywords: ['create', 'mission', 'new', 'add'],
      category: 'actions',
      requiresRole: ['Leader', 'Admin']
    },
    {
      id: 'action-user-management',
      title: 'Manage Users',
      description: 'View and manage organization members',
      icon: <Users size={20} />,
      action: () => navigate('/user-management'),
      keywords: ['users', 'manage', 'members', 'admin'],
      category: 'actions',
      requiresRole: ['Leader', 'Admin']
    }
  ];

  // Filter actions based on permissions and search
  const getFilteredActions = (): QuickAction[] => {
    const allActions = [...getAllActions(), ...kbArticles];
    
    return allActions.filter(action => {
      // Check role requirements
      if (action.requiresRole && !action.requiresRole.includes(userRole)) {
        return false;
      }

      // Check flag requirements
      if (action.requiresFlag && !action.requiresFlag.some(flag => userFlags.includes(flag))) {
        return false;
      }

      // Check search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          action.title.toLowerCase().includes(query) ||
          action.description.toLowerCase().includes(query) ||
          action.keywords.some(keyword => keyword.includes(query))
        );
      }

      return true;
    });
  };

  const filteredActions = getFilteredActions();

  // Fetch KB articles when search query changes (debounced)
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setKbArticles([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingArticles(true);
      try {
        const response = await axios.get('/knowledgebase/articles', {
          params: { search: searchQuery, pageSize: 5 }
        });
        
        const articleActions: QuickAction[] = response.data.articles.map((article: any) => ({
          id: `kb-article-${article.id}`,
          title: article.title,
          description: article.excerpt || `${article.category} • ${article.skillLevel || 'All Levels'}`,
          icon: <BookOpen size={20} />,
          action: () => navigate(`/knowledge/${article.slug}`),
          keywords: [article.title.toLowerCase(), article.category.toLowerCase()],
          category: 'articles' as const
        }));

        setKbArticles(articleActions);
      } catch (error) {
        console.error('Failed to fetch KB articles:', error);
        setKbArticles([]);
      } finally {
        setLoadingArticles(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery, navigate]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredActions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredActions.length) % filteredActions.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredActions[selectedIndex]) {
          filteredActions[selectedIndex].action();
          handleClose();
        }
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredActions]);

  // Focus search input when modal opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when modal closes
  const handleClose = () => {
    setSearchQuery('');
    setSelectedIndex(0);
    onClose();
  };

  if (!isOpen) return null;

  // Group actions by category
  const groupedActions = filteredActions.reduce((acc, action) => {
    if (!acc[action.category]) {
      acc[action.category] = [];
    }
    acc[action.category].push(action);
    return acc;
  }, {} as Record<string, QuickAction[]>);

  const categoryLabels = {
    navigation: 'Navigation',
    actions: 'Quick Actions',
    profile: 'Profile',
    articles: 'Knowledge Base Articles'
  };

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} onClick={handleClose} />

      {/* Modal */}
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.searchContainer}>
            <Search size={20} className={styles.searchIcon} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search for actions, pages, or KB articles..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedIndex(0);
              }}
              className={styles.searchInput}
            />
            <div className={styles.shortcutHint}>
              <Command size={14} />
              <span>K</span>
            </div>
          </div>
          <button onClick={handleClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalContent}>
          {filteredActions.length === 0 ? (
            <div className={styles.emptyState}>
              <Search size={48} />
              <p>No actions found</p>
              <span>Try a different search term</span>
            </div>
          ) : (
            Object.entries(groupedActions).map(([category, actions]) => (
              <div key={category} className={styles.actionGroup}>
                <div className={styles.categoryLabel}>
                  {categoryLabels[category as keyof typeof categoryLabels]}
                </div>
                <div className={styles.actionList}>
                  {actions.map((action, index) => {
                    const globalIndex = filteredActions.indexOf(action);
                    return (
                      <button
                        key={action.id}
                        className={`${styles.actionItem} ${globalIndex === selectedIndex ? styles.actionItemSelected : ''}`}
                        onClick={() => {
                          action.action();
                          handleClose();
                        }}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                      >
                        <div className={styles.actionIcon}>{action.icon}</div>
                        <div className={styles.actionContent}>
                          <div className={styles.actionTitle}>{action.title}</div>
                          <div className={styles.actionDescription}>{action.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerHint}>
            <span className={styles.kbd}>↑↓</span> Navigate
            <span className={styles.kbd}>Enter</span> Select
            <span className={styles.kbd}>Esc</span> Close
          </div>
        </div>
      </div>
    </>
  );
};

export default QuickActionsModal;
